import { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { useQuery } from '@tanstack/react-query'
import { Search, User, ChevronDown, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { LoadingSpinner } from '@/components/ui/loading-spinner'
import { apiClient } from '@/lib/api-client'

// Debounce hook
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value)

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value)
    }, delay)

    return () => {
      clearTimeout(handler)
    }
  }, [value, delay])

  return debouncedValue
}

interface User {
  id: number
  name: string
  email: string
}

interface UserSelectProps {
  value?: string
  onChange: (userId: string) => void
  placeholder?: string
  disabled?: boolean
}

export function UserSelect({ value, onChange, placeholder = "Search users...", disabled = false }: UserSelectProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedUser, setSelectedUser] = useState<User | null>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const [menuStyle, setMenuStyle] = useState<{ top: number; left: number; width: number } | null>(null)

  // Debounce search query to avoid too many API calls
  const debouncedSearchQuery = useDebounce(searchQuery, 300)

  // Fetch users with search and pagination
  const { data: usersData, isLoading } = useQuery({
    queryKey: ['users-search', debouncedSearchQuery],
    queryFn: async () => {
      const response = await apiClient.get('/users', {
        params: {
          search: debouncedSearchQuery,
          limit: 20
        }
      })
      return response.data
    },
    enabled: isOpen,
    staleTime: 30000 // Cache for 30 seconds
  })

  // Show loading when user is typing but debounced query hasn't updated yet
  const isTyping = searchQuery !== debouncedSearchQuery

  const users = usersData || []

  const placeMenu = () => {
    const anchor = dropdownRef.current
    if (!anchor) return
    const rect = anchor.getBoundingClientRect()
    const menuHeight = 240
    const spaceBelow = window.innerHeight - rect.bottom
    const top = spaceBelow < menuHeight + 8 && rect.top > menuHeight
      ? rect.top - menuHeight - 4
      : rect.bottom + 4
    setMenuStyle({ top, left: rect.left, width: rect.width })
  }

  useEffect(() => {
    if (!isOpen) return
    placeMenu()
    const handlePointer = (event: MouseEvent) => {
      const target = event.target as Node
      if (dropdownRef.current?.contains(target) || menuRef.current?.contains(target)) return
      setIsOpen(false)
    }
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false)
        inputRef.current?.blur()
      }
    }
    document.addEventListener('mousedown', handlePointer)
    document.addEventListener('keydown', handleEscape)
    window.addEventListener('resize', placeMenu)
    window.addEventListener('scroll', placeMenu, true)
    return () => {
      document.removeEventListener('mousedown', handlePointer)
      document.removeEventListener('keydown', handleEscape)
      window.removeEventListener('resize', placeMenu)
      window.removeEventListener('scroll', placeMenu, true)
    }
  }, [isOpen])

  // Find selected user when value changes
  useEffect(() => {
    if (value && users.length > 0) {
      const user = users.find((u: User) => u.id.toString() === value)
      if (user) {
        setSelectedUser(user)
      }
    } else {
      setSelectedUser(null)
    }
  }, [value, users])

  const handleUserSelect = (user: User) => {
    setSelectedUser(user)
    onChange(user.id.toString())
    setIsOpen(false)
    setSearchQuery('')
  }

  const handleClear = () => {
    setSelectedUser(null)
    onChange('')
    setSearchQuery('')
  }

  const handleInputFocus = () => {
    setIsOpen(true)
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value)
    if (!isOpen) {
      setIsOpen(true)
    }
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <div className="relative">
        <div className="flex items-center space-x-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              ref={inputRef}
              value={isOpen ? searchQuery : (selectedUser ? `${selectedUser.name} (${selectedUser.email})` : '')}
              onChange={handleInputChange}
              onFocus={handleInputFocus}
              placeholder={selectedUser ? undefined : placeholder}
              disabled={disabled}
              className="pl-10 pr-10"
            />
            {selectedUser && !isOpen && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleClear}
                className="absolute right-1 top-1/2 transform -translate-y-1/2 h-6 w-6 p-0 hover:bg-destructive hover:text-destructive-foreground"
              >
                <X className="h-3 w-3" />
              </Button>
            )}
            {!selectedUser && (
              <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
            )}
          </div>
        </div>
      </div>

      {isOpen && menuStyle && createPortal(
        <div
          ref={menuRef}
          style={{ top: menuStyle.top, left: menuStyle.left, width: menuStyle.width }}
          className="fixed z-[200] max-h-60 overflow-auto rounded-md border border-border bg-popover text-popover-foreground shadow-lg"
        >
          {isLoading || isTyping ? (
            <div className="flex items-center justify-center p-4">
              <LoadingSpinner size="sm" />
              <span className="ml-2 text-sm text-muted-foreground">
                {isTyping ? 'Typing...' : 'Searching users...'}
              </span>
            </div>
          ) : users.length > 0 ? (
            <div className="py-1">
              {users.map((user: User) => (
                <button
                  key={user.id}
                  type="button"
                  onClick={() => handleUserSelect(user)}
                  className="w-full px-4 py-2 text-left hover:bg-muted focus:bg-muted focus:outline-none flex items-center space-x-3"
                >
                  <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <User className="h-4 w-4 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{user.name}</p>
                    <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                  </div>
                  {selectedUser?.id === user.id && (
                    <div className="w-2 h-2 bg-primary rounded-full flex-shrink-0" />
                  )}
                </button>
              ))}
            </div>
          ) : (
            <div className="px-4 py-3 text-sm text-muted-foreground text-center">
              {searchQuery ? 'No users found' : 'Start typing to search users'}
            </div>
          )}
        </div>,
        document.body,
      )}
    </div>
  )
}
